import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
    UntilDestroy,
    untilDestroyed,
} from '@ngneat/until-destroy';
import { first } from 'rxjs';

import { IconComponent } from '~/app/shared/components/elements/icon/icon.component';
import { NameInitialsPipe } from '~/app/shared/pipes/name-initials/name-initials.pipe';
import { ProfilePhotoResult } from '../../modals/modal-profile-photo/modal-profile-photo.component';
import { ModalProfilePhotoService } from '../../services/modal-profile-photo.service';

@UntilDestroy()
@Component({
    selector: 'eb-profile-photo',
    templateUrl: './profile-photo.component.html',
    styleUrls: ['./profile-photo.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IconComponent,
        NameInitialsPipe,
        TranslocoPipe,
    ],
})
export class ProfilePhotoComponent {
    private modalProfilePhotoService = inject(ModalProfilePhotoService);

    public readonly firstName = input('');

    public readonly lastName = input('');

    /** Photo courante : `File` fraîchement recadré, URL distante (lecture), ou `null` (aucune). */
    public readonly photo = input<File | string | null>(null);

    /** Émet le `File` recadré (round-avatar) ou `null` à la suppression. */
    public readonly photoChange = output<File | null>();

    protected readonly photoUrl = signal<string | null>(null);

    constructor() {
        // Garde une URL d'aperçu synchronisée avec la photo, en révoquant les ObjectURL temporaires.
        effect((onCleanup) => {
            const current = this.photo();
            if (!current) {
                this.photoUrl.set(null);
                return;
            }
            if (typeof current === 'string') {
                this.photoUrl.set(current);
                return;
            }
            const objectUrl = URL.createObjectURL(current);
            this.photoUrl.set(objectUrl);
            onCleanup(() => URL.revokeObjectURL(objectUrl));
        });
    }

    /** Ouvre la popin de gestion (Modifier / Mise à jour / Supprimer + édition). */
    public openManager(): void {
        const modalRef = this.modalProfilePhotoService.open({
            photo: this.photo(),
            firstName: this.firstName(),
            lastName: this.lastName(),
        });

        modalRef.afterClosed$
            .pipe(first(), untilDestroyed(this))
            .subscribe((result: ProfilePhotoResult | 'CANCELED' | undefined) => {
                if (!result || typeof result !== 'object') {
                    return; // X / annulation → aucun changement
                }
                this.photoChange.emit(result.type === 'updated' ? result.file : null);
            });
    }
}
